import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import CustomConfirmationModal from '../../../components/CustomConfirmationModal'
import RpcsForm from './RpcsForm'
import FormModal from '../../FormModal'
import MiniSwitch from '../../formUtils/MiniSwitch'
import { useApi } from '../../../hooks/useApi'
import Network from '../../general/Network'

interface Data {
  _id: string
  active: boolean
  chainId: number
  score: number
  createdAt: string
  updatedAt: string
  type: string
  url: string
}

const Rpcs: React.FC = () => {
  const { callApi } = useApi()
  const [data, setData] = useState<Data[] | null>(null)
  const [deleteParams, setDeleteParams] = useState<Data | null>(null)
  const [addNew, setAddNew] = useState(false)
  const [hideInactive, setHideInactive] = useState(true)

  const compareObjects = (a: Data, b: Data): number => {
    if (a.chainId !== b.chainId) return a.chainId - b.chainId
    if (a.type !== b.type) return a.type.localeCompare(b.type)
    return a.active === b.active ? 0 : a.active ? 1 : -1
  }

  const fetchData = async () => {
    try {
      const response = await callApi<Data[]>('/web3/rpc-endpoints')
      if (response) {
        response.sort(compareObjects)
        setData(response)
      }
    } catch (error) {
      console.error('Rpc data error:', error)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleConfirmDelete = async () => {
    if (!deleteParams) return
    try {
      await callApi(`/web3/rpc-endpoints/${deleteParams._id}`, {}, 'DELETE')
      fetchData()
    } catch (error) {
      console.error('There was an error deleting the RPC endpoint:', error)
    }
    setDeleteParams(null)
  }

  const handleToggleActive = async (item: Data) => {
    try {
      await callApi(`/web3/rpc-endpoints/${item._id}`, { ...item, active: !item.active }, 'PUT')
      fetchData()
    } catch (error) {
      console.error('There was an error updating the RPC endpoint:', error)
    }
  }

  if (!data) return <LoaderBar />

  return (
    <Wrap>
      <Header>
        <CheckLabel>
          <input type="checkbox" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} />
          Hide inactive
        </CheckLabel>
        <AddBtn onClick={() => setAddNew(!addNew)}>
          {addNew ? 'Hide form' : '+ Add RPC'}
        </AddBtn>
      </Header>

      {addNew && (
        <FormModal onConfirm={() => { fetchData(); setAddNew(false) }} onCancel={() => setAddNew(false)}>
          <RpcsForm onSuccess={() => { fetchData(); setAddNew(false) }} />
        </FormModal>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Network</Th>
              <Th>Type</Th>
              <Th>Score</Th>
              <Th>URL</Th>
              <Th>Active</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {data.filter((item) => hideInactive ? item.active : true).map((item) => (
              <tr key={item._id}>
                <Td><Network network={item.chainId} /></Td>
                <Td><Mono>{item.type}</Mono></Td>
                <Td><Mono>{item.score !== 0 ? item.score.toFixed(3) : 0}</Mono></Td>
                <Td><UrlText>{item.url}</UrlText></Td>
                <Td><MiniSwitch ison={item.active} onToggle={() => handleToggleActive(item)} /></Td>
                <Td><DeleteBtn onClick={() => setDeleteParams(item)}>Delete</DeleteBtn></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {deleteParams !== null && (
        <CustomConfirmationModal
          message="Are you sure you want to delete this RPC endpoint?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteParams(null)}
        />
      )}
    </Wrap>
  )
}

export default Rpcs

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`
const Header = styled.div`display: flex; justify-content: flex-end; align-items: center; gap: 8px;`

const CheckLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: #777; cursor: pointer; user-select: none;
  &:hover { color: #a0a0a0; }
  input[type='checkbox'] { accent-color: #6f5ce6; cursor: pointer; }
`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 12px; color: #a0a0a0;`
const UrlText = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 10px; color: #777; word-break: break-all;`
const ActionBtn = styled.button`height: 26px; padding: 0 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer; transition: border-color 0.15s, color 0.15s;`
const DeleteBtn = styled(ActionBtn)`&:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }`

const AddBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background 0.15s; &:hover { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
`
